import { AbstractClient, CompositionClient } from "@/app/client";
import type {
  ChatSessionCreate,
  ChatSessionResponse,
  ChatSessionSummary,
  ChatSessionUpdate,
  ChatStatusResponse,
  SendMessageRequest,
  SendMessageResponse,
  UploadMediaResponse,
} from "@/app/api/types/chat-sessions";

export default class APIChatSessions extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  // Create a new chat session
  create = async (body?: ChatSessionCreate): Promise<ChatSessionResponse> => {
    const res = await this._fetch({
      url: `/chat-sessions/`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body: body || {},
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  // List all chat sessions for the current user
  list = async (
    limit: number = 50,
    offset: number = 0,
    session_type?: string,
  ): Promise<ChatSessionSummary[]> => {
    let url = `/chat-sessions/?limit=${limit}&offset=${offset}`;
    if (session_type) {
      url += `&session_type=${session_type}`;
    }
    const res = await this._fetch({
      url,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  // Get a specific chat session
  get = async (sessionId: string): Promise<ChatSessionResponse> => {
    const res = await this._fetch({
      url: `/chat-sessions/${sessionId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  // Send a message to a chat session
  send = async (
    sessionId: string,
    body: SendMessageRequest,
  ): Promise<SendMessageResponse> => {
    const res = await this._fetch({
      url: `/chat-sessions/${sessionId}/send`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  // Poll for status updates
  getStatus = async (sessionId: string): Promise<ChatStatusResponse> => {
    const res = await this._fetch({
      url: `/chat-sessions/${sessionId}/status`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  // Interrupt a running session
  interrupt = async (sessionId: string): Promise<{ message: string }> => {
    const res = await this._fetch({
      url: `/chat-sessions/${sessionId}/interrupt`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  // Update session metadata
  update = async (
    sessionId: string,
    body: ChatSessionUpdate,
  ): Promise<ChatSessionResponse> => {
    const res = await this._fetch({
      url: `/chat-sessions/${sessionId}`,
      method: "PATCH",
      auth: ["OAuth2PasswordBearer"],
      body,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  // Delete a chat session
  delete = async (sessionId: string): Promise<{ message: string }> => {
    const res = await this._fetch({
      url: `/chat-sessions/${sessionId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };

  // Upload media files for a chat message
  uploadMedia = async (
    sessionId: string,
    files: File[],
  ): Promise<UploadMediaResponse> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    const res = await this._fetch({
      url: `/chat-sessions/${sessionId}/upload-media`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body: formData,
      bodyMime: "multipart/form-data",
    });
    if (res.headers.get("Content-Type")?.includes("application/json"))
      return res.json();
    throw new Error("Bad content type");
  };
}
